import { Link } from 'react-router-dom';
import { useContent } from '../App.jsx';
import { ArrowIcon, CalendarIcon, LocationIcon, MusicIcon } from '../components/Icons.jsx';

export default function HomePage() {
  const { content } = useContent();
  const featured = content.instructors.filter((item) => item.visible && item.featured).slice(0, 6);
  const visibleNews = content.news.filter((item) => item.visible);
  const primaryVenue = content.venues.find((venue) => venue.visible && venue.primary) || content.venues.find((venue) => venue.visible);

  return (
    <>
      <section className="hero">
        <img className="hero-image" src={content.media.homeHero} alt="Pocono Workshop instructors performing for an audience" />
        <div className="hero-shade" />
        <div className="hero-content page-width">
          <div className="hero-copy">
            <p className="eyebrow eyebrow-light">{content.event.eyebrow}</p>
            <h1>{content.event.heroTitle}</h1>
            <p className="hero-body">{content.event.heroBody}</p>
            <div className="hero-actions">
              <Link className="button" to="/register">Register for {content.event.year} <ArrowIcon /></Link>
              <Link className="button button-ghost" to="/instructors">Meet the instructors</Link>
            </div>
          </div>
          <div className="hero-details" aria-label="Event details">
            <div><CalendarIcon /><span><small>When</small>{content.event.displayDate}</span></div>
            <div><LocationIcon /><span><small>Where</small>King of Prussia, PA</span></div>
            <div><MusicIcon /><span><small>For</small>Pipers & drummers</span></div>
          </div>
        </div>
        <div className="hero-scroll"><span>Scroll to explore</span><i /></div>
      </section>

      {content.sections.story && (
        <section className="section story-section page-width">
          <div className="story-copy reveal-copy">
            <p className="eyebrow">{content.story.eyebrow}</p>
            <h2>{content.story.title}</h2>
            <p className="large-copy">{content.story.body}</p>
            <Link className="text-link" to="/schedule">See the weekend <ArrowIcon /></Link>
          </div>
          <div className="story-visual">
            <img src={content.media.story} alt="Pipers and drummers performing together" />
            <div className="story-card"><span>Est.</span><strong>2015</strong><p>A decade of better music and lasting friendships.</p></div>
          </div>
          <div className="stat-row">
            <div><strong>{content.story.statOneValue}</strong><span>{content.story.statOneLabel}</span></div>
            <div><strong>{content.story.statTwoValue}</strong><span>{content.story.statTwoLabel}</span></div>
            <div><strong>{content.story.statThreeValue}</strong><span>{content.story.statThreeLabel}</span></div>
          </div>
        </section>
      )}

      {content.sections.instructors && (
        <section className="section instructors-preview">
          <div className="page-width">
            <div className="section-heading split-heading">
              <div><p className="eyebrow">The people in the room</p><h2>Learn from players who shape the art.</h2></div>
              <p>Direct, generous instruction from championship performers and leaders at every level of piping and drumming.</p>
            </div>
            <div className="portrait-grid">
              {featured.map((instructor) => (
                <article className="portrait-card" key={instructor.id}>
                  <img src={instructor.image} alt={instructor.name} />
                  <div><p>{instructor.discipline}</p><h3>{instructor.name}</h3></div>
                </article>
              ))}
            </div>
            <div className="section-end"><Link className="button button-dark" to="/instructors">Meet the full faculty <ArrowIcon /></Link></div>
          </div>
        </section>
      )}

      {content.sections.schedule && (
        <section className="section weekend-section page-width">
          <div className="section-heading split-heading">
            <div><p className="eyebrow">The weekend</p><h2>Arrive inspired. Leave transformed.</h2></div>
            <p>{content.schedule.intro}</p>
          </div>
          <div className="weekend-grid">
            {content.schedule.days.map((day, index) => (
              <article key={day.day}>
                <span className="day-index">0{index + 1}</span>
                <p>{day.date}</p>
                <h3>{day.day}</h3>
                <ul>{day.items.map((item) => <li key={item}>{item}</li>)}</ul>
              </article>
            ))}
          </div>
          <Link className="text-link" to="/schedule">View the schedule <ArrowIcon /></Link>
        </section>
      )}

      {content.sections.gallery && (
        <section className="gallery-band" aria-label="Workshop gallery">
          <img src={content.media.galleryOne} alt="Pocono Workshop instructional staff" />
          <img src={content.media.galleryTwo} alt="Pocono Workshop performance" />
          <div className="gallery-quote"><blockquote>“The music gets us here. The people bring us back.”</blockquote><span>Pocono Workshop</span></div>
        </section>
      )}

      {content.sections.news && visibleNews.length > 0 && (
        <section className="section news-section page-width">
          <div className="section-heading"><p className="eyebrow">Good to know</p><h2>Make the most of the weekend.</h2></div>
          <div className="news-grid">
            {visibleNews.map((item, index) => (
              <article key={item.id}><span>0{index + 1}</span><p className="news-date">{item.dateLabel}</p><h3>{item.title}</h3><p>{item.body}</p></article>
            ))}
          </div>
        </section>
      )}

      {content.sections.location && primaryVenue && (
        <section className="location-feature">
          <img src={content.media.venue} alt="Sheraton Valley Forge Hotel" />
          <div className="location-feature-copy">
            <p className="eyebrow eyebrow-light">Stay where the music happens</p>
            <h2>{primaryVenue.name}</h2>
            <p>{primaryVenue.address}</p>
            <div className="location-actions"><Link className="button" to="/location">Hotel details <ArrowIcon /></Link><a className="text-link light-link" href={primaryVenue.bookingUrl} target="_blank" rel="noreferrer">Book your room</a></div>
          </div>
        </section>
      )}

      <section className="final-cta">
        <div className="page-width"><p className="eyebrow eyebrow-light">{content.event.displayDate}</p><h2>Your next breakthrough might be one weekend away.</h2><Link className="button" to="/register">Save your place <ArrowIcon /></Link></div>
      </section>
    </>
  );
}
